/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import style from '../../../../../../assets/styles/createQuestion.module.css';
import QuillEditor from '../../../../../atoms/QuillEditor';
import arrowIcon from '../../../../../../assets/images/upArrow.svg';
import MathJaxStaticRender from '../../../../../reusableComponents/MathJsxRender';

// Answer format constants
const ANSWER_FORMAT = {
  NUMERIC: 0,
  ALPHANUMERIC: 2,
  USER_KEYBOARD: 4,
};

interface FractionDecimalInputProps {
  body: any[];
  questionbutton: number;
  // eslint-disable-next-line no-unused-vars
  setbodyformultiquestion: (body: any[]) => void;
}

const FractionDecimalInput: React.FC<FractionDecimalInputProps> = ({
  body,
  questionbutton,
  setbodyformultiquestion,
}) => {
  const [questionIdentifier, setQuestionIdentifier] = useState<string | undefined>(
    body[questionbutton]?.question_short_identifier
  );
  const [questionDirection, setQuestionDirection] = useState<string | undefined>(
    body[questionbutton]?.question_direction
  );
  const [compose_text, setCompose_text] = useState<string | undefined>(body[questionbutton]?.compose_question);
  const [explanation_text, setExplanation_text] = useState<string | undefined>(body[questionbutton]?.explaination_text);
  const [videoId, setVideoId] = useState<string | undefined>(body[questionbutton]?.video_id);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const editorRef = useRef<any>(null);

  /* ----------------Latex Preview States ---------------- */
  const [showComposePreview, setShowComposePreview] = useState(false);
  const [showQuestionDirectionPreview, setShowQuestionDirectionPreview] = useState(false);
  const [showExplanationPreview, setShowExplanationPreview] = useState(false);

  // Use the existing selected value or default to 'Select'
  const [answer_format, setAnswer_format] = useState<any>(body[questionbutton]?.answer_format ?? 'Select');

  // Single correct answer field
  const [correct_answer, setCorrect_answer] = useState<string>(body[questionbutton]?.correct_answer ?? '');
  const [answerError, setAnswerError] = useState<string>('');

  // Handle answer format dropdown change
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === 'Select') {
      setAnswer_format(event.target.value);
    } else {
      const answerFormat = Number(event.target.value);
      setAnswer_format(answerFormat);
    }
    // Reset answer and error when format changes
    setCorrect_answer('');
    setAnswerError('');
  };

  // Validate the correct answer input based on the selected answer format and fraction rules
  const validateAnswer = (value: string, format: any): string => {
    if (value === '') return '';

    // Only allow numbers (0-9), forward slash (/), and negative sign (-)
    if (!/^[0-9/-]+$/.test(value)) {
      return 'Only numbers (0-9), forward slash (/), and negative sign (-) are allowed.';
    }

    // Multiple slashes validation (shown for all formats)
    if ((value.match(/\//g) || []).length > 1) {
      return 'Multiple slashes are not allowed.';
    }

    if (format === ANSWER_FORMAT.NUMERIC) {
      // Must be either a signed integer or a signed fraction
      const numericFractionRegex = /^-?\d+(\/-?\d+)?$/;
      if (!numericFractionRegex.test(value)) {
        return 'Numeric format accepts integers or fractions only (e.g., 1/2, -5/6, 42).';
      }

      // If fraction, validate denominator is not zero
      if (value.includes('/')) {
        const parts = value.split('/');
        const den = parseInt(parts[1], 10);
        if (den === 0) {
          return 'Denominator cannot be zero.';
        }
      }
    }
    return '';
  };

  // Handle correct answer input change with real-time validation
  const handleCorrectAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const error = validateAnswer(value, answer_format);
    setAnswerError(error);
    setCorrect_answer(value);
  };

  // Get placeholder based on format
  const getAnswerPlaceholder = (): string => {
    if (answer_format === ANSWER_FORMAT.NUMERIC) return 'Enter integer or fraction (e.g. 1/2, 42)';
    if (answer_format === ANSWER_FORMAT.USER_KEYBOARD) return 'Enter integer or fraction (e.g. 1/2, 42)';
    return 'Enter correct answer';
  };

  const AddUpdateBody = () => {
    const updatebody = [...body];
    updatebody[questionbutton] = {
      idx: questionbutton,
      question_type_id: body[questionbutton]?.question_type_id,
      question_short_identifier: questionIdentifier,
      question_direction: questionDirection,
      compose_question: compose_text,
      correct_answer: correct_answer,
      last_option_idx: null,
      explaination_video: null,
      explaination_text: explanation_text || null,
      answer_format: answer_format,
      task_label: null,
      video_id: videoId || null,
    };
    setbodyformultiquestion(updatebody);
  };

  useEffect(() => {
    AddUpdateBody();
  }, [questionIdentifier, questionDirection, compose_text, correct_answer, answer_format, videoId, explanation_text]);

  function convertToBase64(str: string) {
    return btoa(unescape(encodeURIComponent(str)));
  }

  function isBase64Check(str: string): boolean {
    try {
      return btoa(atob(str)) === str;
    } catch (e) {
      return false;
    }
  }

  function b64DecodeUnicode(str: string) {
    if (!isBase64Check(str)) {
      return str;
    }

    return decodeURIComponent(
      atob(str)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  }

  const decode = (val?: string) => (val ? b64DecodeUnicode(val) : '');

  return (
    <div className={style.tinymce_text} style={{ marginTop: '15px' }}>
      {/* add Question Short Identifier */}
      <div className={style.Question_Direction}>
        <p>
          Question Short Identifier <b className={style.red_mendantory}>*</b>
        </p>
        <textarea
          placeholder="Enter Question Short Identifier"
          className={`${style.question_identifier} ${style.Shortquestion} ${style.nonResizable}`}
          value={questionIdentifier}
          onChange={(e) => setQuestionIdentifier(e.target.value)}
        />
      </div>

      {/* add Question Direction */}
      <div className={`${style.Question_Direction} ${style.QuestionDirection}`}>
        <div className={style.default_itemcenter_and_spacebetween}>
          <p>Enter Question Direction</p>
          <button
            type="button"
            className={style.convert_btn}
            onClick={() => setShowQuestionDirectionPreview((prev) => !prev)}
          >
            {showQuestionDirectionPreview ? 'Edit' : 'Convert'}
          </button>
        </div>
        <div style={{ height: 'auto', flexGrow: '1' }}>
          {!showQuestionDirectionPreview ? (
            <QuillEditor defaultValue={decode(questionDirection)} onChange={(text) => setQuestionDirection(text)} />
          ) : (
            <MathJaxStaticRender html={decode(questionDirection)} />
          )}
        </div>
      </div>

      {/* add compose_text */}
      <div className={style.compose_text}>
        <div className={style.compose_header_row}>
          <p>
            Compose Question <b className={style.red_mendantory}>*</b>
          </p>
          <button type="button" className={style.convert_btn} onClick={() => setShowComposePreview((prev) => !prev)}>
            {showComposePreview ? 'Edit' : 'Convert'}
          </button>
        </div>
        <div style={{ height: 'auto', flexGrow: '1' }}>
          {!showComposePreview ? (
            <QuillEditor
              defaultValue={decode(compose_text)}
              onChange={(text) => setCompose_text(convertToBase64(text))}
            />
          ) : (
            <MathJaxStaticRender html={decode(compose_text)} />
          )}
        </div>
      </div>

      {/* choose answer type*/}
      <div className={style.Fractionanswertype}>
        <p>
          Choose Answer Format <b className={style.red_mendantory}>*</b>
        </p>
        <div style={{ position: 'relative' }}>
          <select
            id="answer-format"
            data-testid="answer-format-select"
            value={answer_format}
            onChange={handleChange}
            className={style.answerFormatSelect}
          >
            <option value="Select" disabled hidden>
              Select
            </option>
            <option value={0}>Numeric</option>
            <option value={4}>User Keyboard</option>
          </select>
          {/* Custom Arrow Icon */}
          <img src={arrowIcon} alt="Custom Arrow" className={style.dropdownArrow} />
        </div>
      </div>

      {/* enter correct answer — single input with format-based validation */}
      <div className={style.correctAnswer}>
        <p>
          Enter Correct Answer <b className={style.red_mendantory}>*</b> <br />
          <span className={style.sentenceNote}>
            Note: The correct answer should be entered in its lowest fraction form.
          </span>
        </p>
        <div className={style.fractionInput}>
          <input
            className={`${style.NumeratorInput} ${answerError ? style.inputError : ''}`}
            type="text"
            placeholder={getAnswerPlaceholder()}
            value={correct_answer}
            disabled={answer_format === 'Select'}
            onChange={handleCorrectAnswerChange}
            data-testid="correct-answer-input"
            style={{ width: '100%' }}
          />
        </div>
        {answerError && (
          <p className={style.errorText} style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
            {answerError}
          </p>
        )}
      </div>

      <span className={style.seprator}></span>
      {/* add Video ID */}
      <div className={`${style.explanation_text} ${style.videoId}`}>
        <p>Video ID</p>
        <div className={style.explanation_video}>
          <input
            type="text"
            value={videoId}
            placeholder="Enter Video ID"
            onChange={(e) => setVideoId(e.target.value)}
          />
        </div>
      </div>

      {/* add explanation_text */}
      <div className={style.explanation_text}>
        <div className={style.default_itemcenter_and_spacebetween}>
          <p>Add Explanation Text</p>
          <button type="button" className={style.convert_btn} onClick={() => setShowExplanationPreview((p) => !p)}>
            {showExplanationPreview ? 'Edit' : 'Convert'}
          </button>
        </div>
        <div style={{ height: 'auto', flexGrow: '1' }}>
          {!showExplanationPreview ? (
            <QuillEditor
              defaultValue={decode(explanation_text)}
              onChange={(text) => setExplanation_text(convertToBase64(text))}
            />
          ) : (
            <MathJaxStaticRender html={decode(explanation_text)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default FractionDecimalInput;
